import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Flower2, Plus, CheckCircle } from "lucide-react";

const PROMPTS = [
  "What's one thing that went well today, even if it was small?",
  "Who is someone you're grateful to have in your life?",
  "What's a simple pleasure you enjoyed recently?",
  "What's something about your body or health you appreciate?",
  "What's a challenge you've overcome that made you stronger?",
  "What's something in your environment right now that you appreciate?",
];

const GratitudeJournal = () => {
  const [entries, setEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState("");
  const [promptIdx, setPromptIdx] = useState(0);

  const addEntry = () => {
    if (!currentEntry.trim()) return;
    setEntries([...entries, { text: currentEntry, prompt: PROMPTS[promptIdx] }]);
    setCurrentEntry("");
    setPromptIdx((promptIdx + 1) % PROMPTS.length);
  };

  return (
    <div data-testid="gratitude-journal" className="rounded-2xl p-8" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Gratitude Journal</h2>
      <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        Research shows that regular gratitude practice increases happiness by 25% and reduces depressive symptoms. Write three things you're grateful for.
      </p>

      {/* Prompt */}
      <div className="p-4 rounded-xl mb-4" style={{ background: "rgba(247,208,70,0.08)", border: "1px solid rgba(247,208,70,0.15)" }}>
        <p className="text-xs mb-1" style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>Prompt</p>
        <p className="text-sm italic" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{PROMPTS[promptIdx]}</p>
      </div>

      {/* Input */}
      <div className="space-y-3 mb-6">
        <Textarea data-testid="gratitude-input" value={currentEntry} onChange={(e) => setCurrentEntry(e.target.value)}
          placeholder="I'm grateful for..." rows={2}
          className="rounded-xl border resize-none" style={{ borderColor: "var(--ll-border)" }} />
        <button data-testid="gratitude-add" onClick={addEntry} disabled={!currentEntry.trim()}
          className="rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 transition-all hover:-translate-y-0.5"
          style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          <Plus size={14} /> Add Gratitude
        </button>
      </div>

      {/* Entries */}
      {entries.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Your gratitudes ({entries.length})</p>
          {entries.map((entry, i) => (
            <div key={`gratitude-${entry.text.slice(0,20)}-${i}`} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
              <Flower2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: "var(--ll-green)" }} />
              <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{entry.text}</p>
            </div>
          ))}
          {entries.length >= 3 && (
            <div className="p-4 rounded-xl text-center" style={{ background: "rgba(163,184,153,0.1)" }}>
              <CheckCircle size={20} className="mx-auto mb-2" style={{ color: "var(--ll-green)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Beautiful! You've captured 3+ gratitudes.</p>
              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Try to do this daily — it compounds over time.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GratitudeJournal;
